import React, { useEffect, useMemo, useRef } from "react";
import InlineIcon from "../../components/InlineIcon";
import FileModel from "../../models/FileModel";

interface GalleryFile {
  id: number;
  filename: string;
  url: string;
  thumbnailurl: string;
  md5hash: string;
  extension?: string;
}

interface GalleryPhoto {
  tags: string[];
  file: GalleryFile;
}

interface ProductGalleryProps {
  gallery: GalleryPhoto[];
  productName: string;
  productSlug?: string;
  selectedOptionValues?: number[];
  selectedUomId?: number;
  selectedVariantId?: number;
}

const ProductGallery: React.FC<ProductGalleryProps> = ({
  gallery,
  productSlug,
  selectedOptionValues = [],
  selectedUomId,
  selectedVariantId,
}) => {
  const mainSwiperRef = useRef<any>(null);
  const thumbsSwiperRef = useRef<any>(null);

  // Generate unique IDs based on productSlug
  const mainGalleryId = `product-gallery-main-gallery-${
    productSlug || "quickview"
  }`;
  const thumbnailsId = `product-gallery-thumbnails-${
    productSlug || "quickview"
  }`;

  // Gallery is already a flat array, no need to transform
  const allImages = useMemo(() => {
    return gallery || [];
  }, [gallery]);

  // Filter images based on current selections using tags
  const visibleImages = useMemo(() => {
    // If no selections made, show all images
    if (
      selectedOptionValues.length === 0 &&
      !selectedVariantId &&
      !selectedUomId
    ) {
      return allImages;
    }

    const matchingImages: GalleryPhoto[] = [];

    allImages.forEach((photo) => {
      let shouldShow = false;

      // Check if image has matching tags for variant (highest priority)
      if (selectedVariantId) {
        const variantTag = `variant_${selectedVariantId}`;
        if (photo.tags.includes(variantTag)) {
          shouldShow = true;
        }
      }

      // If not matched by variant, check UOM-specific tags
      if (!shouldShow && selectedUomId) {
        const uomTag = `uom_${selectedUomId}`;
        if (photo.tags.includes(uomTag)) {
          shouldShow = true;
        }
      }

      // If not matched by variant or UOM, check option value tags
      if (!shouldShow && selectedOptionValues.length > 0) {
        // Check if image has any of the selected option value tags
        const hasOptionMatch = selectedOptionValues.some((optionValueId) => {
          const optionTag = `product_option_value_${optionValueId}`;
          return photo.tags.includes(optionTag);
        });

        if (hasOptionMatch) {
          shouldShow = true;
        }
      }

      if (shouldShow) {
        matchingImages.push(photo);
      }
    });

    // If no matching images found, show all images as fallback
    if (matchingImages.length === 0) {
      return allImages;
    }

    return matchingImages;
  }, [allImages, selectedOptionValues, selectedUomId, selectedVariantId]);

  // Initialize Swiper when component mounts
  useEffect(() => {
    const initSwiper = async () => {
      try {
        const { Swiper } = await import("swiper");
        const { Navigation, Thumbs } = await import("swiper/modules");

        // Check if DOM elements exist
        const mainElement = document.getElementById(mainGalleryId);
        const thumbsElement = document.getElementById(thumbnailsId);

        if (!mainElement) {
          console.warn(`Main gallery element #${mainGalleryId} not found`);
          return;
        }

        // Initialize thumbs swiper first
        if (visibleImages.length > 1 && thumbsElement) {
          try {
            thumbsSwiperRef.current = new Swiper(`#${thumbnailsId}`, {
              modules: [Navigation],
              direction: "horizontal",
              slidesPerView: 5,
              spaceBetween: 8,
              watchSlidesProgress: true,
              freeMode: true,
              loop: false,
              navigation: {
                nextEl: `#${thumbnailsId} .swiper-button-next`,
                prevEl: `#${thumbnailsId} .swiper-button-prev`,
              },
            });
          } catch (error) {
            console.warn("Error initializing thumbs swiper:", error);
          }
        }

        // Initialize main swiper
        try {
          mainSwiperRef.current = new Swiper(`#${mainGalleryId}`, {
            modules: [Navigation, Thumbs],
            direction: "horizontal",
            slidesPerView: 1,
            spaceBetween: 0,
            loop: false,
            navigation: {
              nextEl: `#${mainGalleryId} .swiper-button-next`,
              prevEl: `#${mainGalleryId} .swiper-button-prev`,
            },
            thumbs: thumbsSwiperRef.current
              ? { swiper: thumbsSwiperRef.current }
              : undefined,
          });
        } catch (error) {
          console.warn("Error initializing main swiper:", error);
        }
      } catch (error) {
        console.warn("Error loading Swiper modules:", error);
      }
    };

    if (visibleImages.length > 0) {
      // Add small delay to ensure DOM is ready
      setTimeout(initSwiper, 100);
    }

    return () => {
      try {
        if (mainSwiperRef.current && mainSwiperRef.current.destroy) {
          mainSwiperRef.current.destroy(true, true);
        }
        if (thumbsSwiperRef.current && thumbsSwiperRef.current.destroy) {
          thumbsSwiperRef.current.destroy(true, true);
        }
      } catch (error) {
        console.warn("Error destroying swipers:", error);
      }
    };
  }, [visibleImages, mainGalleryId, thumbnailsId]);

  // Listen for product option changes to update gallery
  useEffect(() => {
    const handleOptionChange = () => {
      // Only update if swipers are properly initialized
      if (
        mainSwiperRef.current &&
        typeof mainSwiperRef.current.update === "function"
      ) {
        try {
          mainSwiperRef.current.update();
        } catch {
          // Silently handle errors
        }
      }
      if (
        thumbsSwiperRef.current &&
        typeof thumbsSwiperRef.current.update === "function"
      ) {
        try {
          thumbsSwiperRef.current.update();
        } catch {
          // Silently handle errors
        }
      }
    };

    window.addEventListener("product:option-changed", handleOptionChange);
    return () => {
      window.removeEventListener("product:option-changed", handleOptionChange);
    };
  }, [mainGalleryId, thumbnailsId]);

  if (!visibleImages || visibleImages.length === 0) {
    return (
      <div className="aspect-square bg-gray-100 rounded-lg flex items-center justify-center">
        <div className="text-center text-gray-500">
          <InlineIcon name="image" className="w-16 h-16 mx-auto mb-2" />
          <p className="text-sm">Không có hình ảnh</p>
        </div>
      </div>
    );
  }

  return (
    <div className="product-gallery">
      <style>
        {`
          .swiper-lazy-preloader {
            display: none !important;
          }
        `}
      </style>
      {/* Main Gallery Swiper */}
      <div
        id={mainGalleryId}
        className={`swiper banner-swiper overflow-hidden group/swiper ${FileModel.getAspectRatio()}`}
        style={
          {
            "--swiper-navigation-size": "24px",
            "--swiper-theme-color": "#000",
          } as React.CSSProperties
        }
        data-swiper-id={mainGalleryId}
        data-thumbs-id={thumbnailsId}
        data-slides-mobile="1"
        data-slides-desktop="1"
        data-gap-mobile="16"
        data-gap-desktop="16"
        data-navigation="1"
        data-pagination="0"
        data-autoplay="0"
        data-delay="4000"
        data-loop="0"
        data-gallery-layout="slider"
        data-zoom="3"
      >
        <div className="swiper-wrapper">
          {visibleImages.map((photo, index) => (
            <div
              key={`${photo.file.md5hash}-${index}`}
              className={`swiper-slide relative ${FileModel.getAspectRatio()}`}
              data-slider-id={mainGalleryId}
              data-banner-id={photo.file.id}
              data-tags={`,${photo.tags.join(",")},`}
            >
              {photo.file.extension === "mp4" ? (
                <video
                  className="absolute inset-0 w-full h-full object-cover rounded-lg"
                  controls
                  preload="metadata"
                  data-src={photo.file.url}
                >
                  <source src={photo.file.url} type="video/mp4" />
                  Trình duyệt của bạn không hỗ trợ video.
                </video>
              ) : (
                <div className="swiper-zoom-container">
                  <img
                    src={FileModel.getThumbnailFromUrl(photo.file.url, 600, 0)}
                    alt={photo.file.filename}
                    className="absolute inset-0 w-full h-full object-cover rounded-lg"
                    data-zoom-src={photo.file.url}
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Navigation */}
        <div
          className={`swiper-button-prev swiper-button-prev-${mainGalleryId} group-hover/swiper:opacity-100 opacity-0 transition-opacity duration-200`}
        >
          <div className="flex items-center justify-center bg-white/30 hover:bg-white/90 rounded-full shadow-md/10 transition-colors duration-200">
            <div className="w-10 h-10 flex items-center justify-center">
              <InlineIcon name="chevron-left" className="w-4 h-4 stroke-1" />
            </div>
          </div>
        </div>
        <div
          className={`swiper-button-next swiper-button-next-${mainGalleryId} group-hover/swiper:opacity-100 opacity-0 transition-opacity duration-200`}
        >
          <div className="flex items-center justify-center bg-white/30 hover:bg-white/90 rounded-full shadow-md/10 transition-colors duration-200">
            <div className="w-10 h-10 flex items-center justify-center">
              <InlineIcon name="chevron-right" className="w-4 h-4 stroke-1" />
            </div>
          </div>
        </div>
      </div>

      {/* Thumbnails Swiper */}
      {visibleImages.length > 1 && (
        <div
          id={thumbnailsId}
          className="swiper banner-swiper overflow-hidden group/swiper mt-4"
          style={
            {
              "--swiper-navigation-size": "18px",
              "--swiper-navigation-sides-offset": "0px",
              "--swiper-navigation-top-offset": "36%",
              "--swiper-theme-color": "#000",
            } as React.CSSProperties
          }
          data-swiper-id={thumbnailsId}
          data-slides-mobile="5"
          data-slides-desktop="5"
          data-gap-mobile="8"
          data-gap-desktop="8"
          data-navigation="1"
          data-direction="horizontal"
        >
          <div className="swiper-wrapper">
            {visibleImages.map((photo, index) => (
              <div
                key={`thumb-${photo.file.md5hash}-${index}`}
                className={`swiper-slide relative ${FileModel.getAspectRatio()}`}
                data-slider-id={thumbnailsId}
                data-banner-id={photo.file.id}
                data-tags={`,${photo.tags.join(",")},`}
              >
                {photo.file.extension === "mp4" ? (
                  <div className="relative w-full h-full">
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20 rounded">
                      <svg
                        className="w-6 h-6 text-white"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>
                ) : (
                  <img
                    src={FileModel.getThumbnailFromUrl(photo.file.url, 200, 0)}
                    alt={photo.file.filename}
                    className="absolute inset-0 object-cover rounded w-full h-full"
                  />
                )}
              </div>
            ))}
          </div>

          {/* Thumbnail Navigation */}
          <div
            className={`swiper-button-prev swiper-button-prev-${thumbnailsId} group-hover/swiper:opacity-100 opacity-0 transition-opacity duration-200`}
          >
            <div className="flex items-center justify-center bg-white/30 hover:bg-white/90 rounded-full shadow-md/10 transition-colors duration-200">
              <div className="w-6 h-6 flex items-center justify-center">
                <InlineIcon name="chevron-left" className="w-4 h-4 stroke-1" />
              </div>
            </div>
          </div>
          <div
            className={`swiper-button-next swiper-button-next-${thumbnailsId} group-hover/swiper:opacity-100 opacity-0 transition-opacity duration-200`}
          >
            <div className="flex items-center justify-center bg-white/30 hover:bg-white/90 rounded-full shadow-md/10 transition-colors duration-200">
              <div className="w-6 h-6 flex items-center justify-center">
                <InlineIcon name="chevron-right" className="w-4 h-4 stroke-1" />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductGallery;
